/**
 * shopStore — Zustand store for shop state (packs, wallet, pity)
 */

import { create } from 'zustand';
import {
  listPacks,
  purchasePack,
  getWallet,
  getPityCounter,
  disenchant,
  craft,
  disenchantDuplicates,
  getDustRates,
  getLoginRewardState,
  claimLoginReward,
  type CardPack,
  type PurchaseResult,
  type PlayerWallet,
  type PityCounter,
  type DisenchantResult,
  type CraftResult,
  type BulkDisenchantResult,
  type DustRates,
  type LoginRewardState,
  type LoginRewardResult,
} from '../services/ShopAPI';

interface ShopState {
  // Data
  packs: CardPack[];
  wallet: PlayerWallet | null;
  pityCounters: Record<string, PityCounter>; // keyed by pack_id
  lastPurchase: PurchaseResult | null;
  dustRates: DustRates | null;
  loginRewardState: LoginRewardState | null;
  lastLoginReward: LoginRewardResult | null;

  // Loading states
  isLoadingPacks: boolean;
  isLoadingWallet: boolean;
  isPurchasing: boolean;
  isDisenchanting: boolean;
  isCrafting: boolean;
  error: string | null;

  // Actions
  fetchPacks: () => Promise<void>;
  fetchWallet: () => Promise<void>;
  fetchPityCounter: (packId: string) => Promise<void>;
  purchase: (packId: string) => Promise<PurchaseResult | null>;
  clearLastPurchase: () => void;
  clearError: () => void;

  // Dust actions
  fetchDustRates: () => Promise<void>;
  disenchantCard: (cardCode: string) => Promise<DisenchantResult | null>;
  craftCard: (cardCode: string) => Promise<CraftResult | null>;
  disenchantAllDuplicates: () => Promise<BulkDisenchantResult | null>;

  // Login reward actions
  fetchLoginReward: () => Promise<void>;
  claimReward: () => Promise<LoginRewardResult | null>;
  clearLastLoginReward: () => void;
}

export const useShopStore = create<ShopState>((set, get) => ({
  // Initial state
  packs: [],
  wallet: null,
  pityCounters: {},
  lastPurchase: null,
  dustRates: null,
  loginRewardState: null,
  lastLoginReward: null,
  isLoadingPacks: false,
  isLoadingWallet: false,
  isPurchasing: false,
  isDisenchanting: false,
  isCrafting: false,
  error: null,

  fetchPacks: async () => {
    set({ isLoadingPacks: true, error: null });
    try {
      const packs = await listPacks();
      set({ packs, isLoadingPacks: false });
    } catch (err) {
      const msg = err instanceof Error ? err.message : '載入卡包失敗';
      set({ isLoadingPacks: false, error: msg });
    }
  },

  fetchWallet: async () => {
    set({ isLoadingWallet: true });
    try {
      const wallet = await getWallet();
      set({ wallet, isLoadingWallet: false });
    } catch (err) {
      const msg = err instanceof Error ? err.message : '載入錢包失敗';
      set({ isLoadingWallet: false, error: msg });
    }
  },

  fetchPityCounter: async (packId: string) => {
    try {
      const pity = await getPityCounter(packId);
      set((state) => ({
        pityCounters: { ...state.pityCounters, [packId]: pity },
      }));
    } catch {
      // Silently ignore — pity display is optional
    }
  },

  purchase: async (packId: string) => {
    set({ isPurchasing: true, error: null });
    try {
      const result = await purchasePack(packId);
      const currentWallet = get().wallet;
      const updates: Partial<ShopState> = {
        isPurchasing: false,
        lastPurchase: result,
        wallet: currentWallet
          ? { ...currentWallet, coins: result.coins_left }
          : null,
      };
      // Update pity counter from purchase result
      if (result.pity) {
        updates.pityCounters = {
          ...get().pityCounters,
          [result.pity.pack_id]: result.pity,
        };
      }
      set(updates);
      return result;
    } catch (err) {
      const msg = err instanceof Error ? err.message : '購買失敗';
      set({ isPurchasing: false, error: msg });
      return null;
    }
  },

  clearLastPurchase: () => { set({ lastPurchase: null }); },
  clearError: () => { set({ error: null }); },

  // --- Dust actions ---

  fetchDustRates: async () => {
    try {
      const rates = await getDustRates();
      set({ dustRates: rates });
    } catch {
      // Silently ignore
    }
  },

  disenchantCard: async (cardCode: string) => {
    set({ isDisenchanting: true, error: null });
    try {
      const result = await disenchant(cardCode);
      // Update wallet dust balance
      const wallet = get().wallet;
      if (wallet) {
        set({ wallet: { ...wallet, dust: result.dust_balance } });
      }
      set({ isDisenchanting: false });
      return result;
    } catch (err) {
      const msg = err instanceof Error ? err.message : '分解失敗';
      set({ isDisenchanting: false, error: msg });
      return null;
    }
  },

  craftCard: async (cardCode: string) => {
    set({ isCrafting: true, error: null });
    try {
      const result = await craft(cardCode);
      // Update wallet dust balance
      const wallet = get().wallet;
      if (wallet) {
        set({ wallet: { ...wallet, dust: result.dust_balance } });
      }
      set({ isCrafting: false });
      return result;
    } catch (err) {
      const msg = err instanceof Error ? err.message : '合成失敗';
      set({ isCrafting: false, error: msg });
      return null;
    }
  },

  disenchantAllDuplicates: async () => {
    set({ isDisenchanting: true, error: null });
    try {
      const result = await disenchantDuplicates();
      // Update wallet dust balance
      const wallet = get().wallet;
      if (wallet) {
        set({ wallet: { ...wallet, dust: result.dust_balance } });
      }
      set({ isDisenchanting: false });
      return result;
    } catch (err) {
      const msg = err instanceof Error ? err.message : '批量分解失敗';
      set({ isDisenchanting: false, error: msg });
      return null;
    }
  },

  // --- Login reward actions ---

  fetchLoginReward: async () => {
    try {
      const state = await getLoginRewardState();
      set({ loginRewardState: state });
    } catch {
      // Silently ignore
    }
  },

  claimReward: async () => {
    set({ error: null });
    try {
      const result = await claimLoginReward();
      // Update wallet
      const wallet = get().wallet;
      if (wallet) {
        set({
          wallet: { ...wallet, coins: result.wallet_coins, dust: result.wallet_dust },
        });
      }
      set({ lastLoginReward: result });
      // Refresh login reward state
      try {
        const state = await getLoginRewardState();
        set({ loginRewardState: state });
      } catch { /* ignore */ }
      return result;
    } catch (err) {
      const msg = err instanceof Error ? err.message : '領取失敗';
      set({ error: msg });
      return null;
    }
  },

  clearLastLoginReward: () => { set({ lastLoginReward: null }); },
}));

// --- Pity helpers ---

/** SR pity threshold (guaranteed SR+ every 10 pulls). */
export const SR_PITY_MAX = 10;

/** SSR hard pity threshold (guaranteed SSR at 90 pulls). */
export const SSR_PITY_MAX = 90;

/** SSR soft pity start (from pull 75). */
export const SSR_SOFT_PITY_START = 75;

/** Get progress toward SR pity (0 to SR_PITY_MAX). */
export function getSRPityProgress(pity: PityCounter | undefined): number {
  return pity ? pity.sr_pity : 0;
}

/** Get progress toward SSR pity (0 to SSR_PITY_MAX). */
export function getSSRPityProgress(pity: PityCounter | undefined): number {
  return pity ? pity.ssr_pity : 0;
}

/** Check if currently in soft pity range. */
export function isInSoftPity(pity: PityCounter | undefined): boolean {
  return pity ? pity.ssr_pity >= SSR_SOFT_PITY_START - 1 : false;
}
